import { type FC } from 'react';
import { BadgeQuestionMark, ChevronsUpDown, LogOut, Moon, Sun } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@zawarski/eti-ui';
import { Avatar, AvatarFallback } from 'components/ui/avatar';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from 'components/ui/sidebar';
import ThemeDetect from '@/hooks/useThemeQuery';

interface Props {
  user: {
    firstName: string;
    lastName: string;
    email: string;
  };
}

const NavUser: FC<Props> = ({ user }) => {
  const { isMobile, setOpenMobile, setOpen } = useSidebar();
  const { isDarkMode, toggleTheme } = ThemeDetect();

  const fullName = `${user.firstName} ${user.lastName}`;
  const avatarInitials = user.firstName.charAt(0) + user.lastName.charAt(0);

  const handleLogout = () => {
    sessionStorage.clear();
    window.location.href = '/';
  };

  const handleContactSupport = () => {
    // Close/collapse sidebar before opening contact support
    if (isMobile) {
      setOpenMobile(false);
    } else {
      setOpen(false);
    }

    window.dispatchEvent(new CustomEvent('open-contact-support'));
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger>
            <SidebarMenuButton
              size='lg'
              className='ci:data-[state=open]:bg-sidebar-accent ci:data-[state=open]:text-sidebar-accent-foreground'>
              <Avatar className='ci:h-8 ci:w-8 ci:rounded-lg'>
                <AvatarFallback>{avatarInitials}</AvatarFallback>
              </Avatar>
              <div className='ci:grid ci:flex-1 ci:text-left ci:text-sm ci:leading-tight'>
                <span className='ci:truncate ci:font-medium'>{fullName}</span>
                <span className='ci:truncate ci:text-xs'>{user.email}</span>
              </div>
              <ChevronsUpDown className='ci:ml-auto ci:size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='ci:w-(--radix-dropdown-menu-trigger-width) ci:min-w-56 ci:rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}>
            <DropdownMenuGroup>
              <DropdownMenuItem className={'ci:cursor-pointer'} onClick={handleContactSupport}>
                <BadgeQuestionMark />
                Contact Support
              </DropdownMenuItem>
              <DropdownMenuItem className={'ci:cursor-pointer'} onClick={toggleTheme}>
                {isDarkMode ? <Sun /> : <Moon />}
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator className={'ci:bg-[var(--muted-foreground)]/30'} />
            <DropdownMenuItem className={'ci:cursor-pointer'} onClick={handleLogout}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
};
export default NavUser;
